import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Shield, Loader2, AlertCircle, Wrench, ClipboardCheck, Copy } from 'lucide-react';
import { useAuth, ROLE_HOMES } from '@/context/AuthContext.jsx';
import { authApi } from '@/services/api.js';
import LanguageSwitcher from '@/components/common/LanguageSwitcher.jsx';

/**
 * Email-OTP verification surface — final step of the signup flow and
 * also the step a user is bounced into when they try to log in before
 * confirming their email.
 *
 * Reads the routed-in email + devOtp from location.state. If the user
 * arrives here directly (no state), we send them back to /login.
 *
 * The yellow Dev Mode banner is rendered ONLY when devOtp is truthy
 * (i.e. the BE included `dev_otp` in its response because the env is
 * non-prod / non-staging). Click-to-copy + Use-this-code auto-fill.
 */
export default function VerifyOtp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeOtp } = useAuth();

  const incomingEmail  = location.state?.email  || '';
  const initialDevOtp  = location.state?.devOtp || null;
  const cameFrom       = location.state?.from   || 'login';

  // NOTE: every hook below must run on EVERY render. The redirect guard for
  // the no-email case used to sit here, above these hooks, which meant this
  // component rendered either 0 hooks or 11 depending on whether the route
  // carried state — the exact shape React reports as "rendered fewer hooks
  // than expected". The guard now lives after the hook block.
  const [email]         = useState(incomingEmail);
  const [digits, setDigits]   = useState(['', '', '', '', '', '']);
  const [devOtp, setDevOtp]   = useState(initialDevOtp);
  const [submit, setSubmit]   = useState(false);
  const [serverErr, setServerErr] = useState(null);
  const [resending, setResending] = useState(false);
  const [resendOk, setResendOk] = useState(null);
  const [copied,   setCopied]   = useState(false);

  const inputRefs = useRef([]);

  // Focus the first box on mount. Skipped when we are about to redirect —
  // the effect still runs unconditionally, the work inside it does not.
  useEffect(() => {
    if (!incomingEmail) return;
    inputRefs.current[0]?.focus();
  }, [incomingEmail]);

  const code = useMemo(() => digits.join(''), [digits]);

  // No email in state → bounce to /login. (Direct navigation case.)
  if (!incomingEmail) return <Navigate to="/login" replace />;

  function setDigit(idx, value) {
    const v = value.replace(/\D/g, '').slice(0, 1);
    setDigits(d => {
      const next = d.slice();
      next[idx] = v;
      return next;
    });
    if (v && idx < 5) inputRefs.current[idx + 1]?.focus();
    if (serverErr) setServerErr(null);
  }

  function onKeyDown(idx, ev) {
    if (ev.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    } else if (ev.key === 'ArrowLeft' && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    } else if (ev.key === 'ArrowRight' && idx < 5) {
      inputRefs.current[idx + 1]?.focus();
    }
  }

  function onPaste(ev) {
    const text = (ev.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!text) return;
    ev.preventDefault();
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < text.length; i++) next[i] = text[i];
    setDigits(next);
    inputRefs.current[Math.min(text.length, 5)]?.focus();
  }

  function fillFromDevOtp(value) {
    const text = String(value || '').replace(/\D/g, '').slice(0, 6);
    if (text.length !== 6) return;
    setDigits(text.split(''));
    inputRefs.current[5]?.focus();
  }

  async function copyDevOtp() {
    try {
      await navigator.clipboard.writeText(String(devOtp || ''));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — ignore */
    }
  }

  async function onSubmit(ev) {
    ev.preventDefault();
    if (code.length !== 6) {
      setServerErr('Enter all 6 digits.');
      return;
    }
    setSubmit(true);
    setServerErr(null);
    try {
      const res = await authApi.verifyOtp(email, code);
      const me  = completeOtp(res);
      const dest = pickHomeForUser(me) || '/';
      navigate(dest, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const data   = err?.response?.data || {};
      if (status === 422) {
        setServerErr(data.message || 'Invalid or expired code.');
      } else if (!err?.response) {
        setServerErr('Could not reach the NACIS API. Check your connection.');
      } else {
        setServerErr(data.message || 'Verification failed. Please try again.');
      }
    } finally {
      setSubmit(false);
    }
  }

  async function onResend() {
    setResending(true);
    setResendOk(null);
    setServerErr(null);
    try {
      const res = await authApi.resendOtp(email);
      setResendOk(res?.message || 'A fresh code has been sent.');
      if (res?.dev_otp) setDevOtp(res.dev_otp);
    } catch (err) {
      const data = err?.response?.data || {};
      setServerErr(data.message || 'Could not resend the code. Try again in a moment.');
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left: brand panel */}
      <aside className="relative hidden bg-slate-950 lg:flex lg:flex-col lg:p-12 lg:text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              'radial-gradient(900px circle at 0% 0%, rgba(56,189,248,0.18), transparent 45%), radial-gradient(800px circle at 100% 100%, rgba(14,165,233,0.18), transparent 45%)',
          }}
          aria-hidden
        />
        <div className="relative flex flex-1 flex-col">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-white/10">
              <Shield className="h-6 w-6" strokeWidth={2.4} />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-bold tracking-wide">NACIS</div>
              <div className="text-[10px] uppercase tracking-[0.14em] opacity-70">National AI Communications-Intelligence System</div>
            </div>
          </Link>

          <div className="mt-auto max-w-md">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-sky-300">Email verification</div>
            <h2 className="mt-3 text-3xl font-semibold leading-tight">
              One quick step before you can submit reports.
            </h2>
            <p className="mt-4 text-sm leading-7 text-slate-300">
              We sent a 6-digit code to your email. Enter it below to confirm
              ownership. The code expires in 10 minutes — request a new one if
              it lapses.
            </p>
          </div>
        </div>
      </aside>

      {/* Right: form panel */}
      <main className="relative flex items-center justify-center px-6 py-12 lg:px-12">
        <div className="absolute right-4 top-4 lg:right-8 lg:top-8">
          <LanguageSwitcher variant="light" />
        </div>
        <div className="w-full max-w-md">
          <div className="mb-2 flex items-center gap-2 lg:hidden">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-900 text-white">
              <Shield className="h-5 w-5" strokeWidth={2.4} />
            </div>
            <div className="text-sm font-bold tracking-wide text-slate-900">NACIS</div>
          </div>

          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            {cameFrom === 'register' ? 'Verify your email' : 'Almost there'}
          </h1>
          <p className="mt-1.5 text-sm text-slate-600">
            Enter the 6-digit code sent to <span className="font-medium text-slate-800">{email}</span>.
          </p>

          {/* ── Dev-mode banner ─────────────────────────────────────── */}
          {devOtp && (
            <div className="mt-6 rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Wrench className="h-4 w-4" />
                Dev mode — email not sent
              </div>
              <div className="mt-3 flex items-center justify-between gap-3 rounded bg-white/60 px-3 py-2">
                <button
                  type="button"
                  onClick={copyDevOtp}
                  className="font-mono text-2xl font-bold tracking-[0.4em] text-amber-900 hover:text-amber-700"
                  title="Click to copy"
                >
                  {devOtp}
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={copyDevOtp}
                    className="inline-flex items-center gap-1 rounded border border-amber-300 bg-white px-2 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
                  >
                    {copied ? <ClipboardCheck className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                  <button
                    type="button"
                    onClick={() => fillFromDevOtp(devOtp)}
                    className="inline-flex items-center rounded bg-amber-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-amber-500"
                  >
                    Use this code
                  </button>
                </div>
              </div>
              <p className="mt-3 text-xs text-amber-800">
                Stage and production environments still email this code normally.
              </p>
            </div>
          )}

          {serverErr && (
            <div role="alert" className="mt-6 flex gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>{serverErr}</div>
            </div>
          )}

          {resendOk && (
            <div role="status" className="mt-6 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              {resendOk}
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-5" noValidate>
            <div>
              <label className="block text-sm font-medium text-slate-800">Verification code</label>
              <div className="mt-2 flex justify-between gap-2" onPaste={onPaste}>
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={el => (inputRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={e => setDigit(i, e.target.value)}
                    onKeyDown={e => onKeyDown(i, e)}
                    className="h-14 w-12 rounded-md border border-slate-300 bg-white text-center text-xl font-semibold text-slate-900 shadow-sm focus:border-sky-500 focus:ring-1 focus:ring-sky-500 disabled:opacity-50"
                    disabled={submit}
                    aria-label={`Digit ${i + 1}`}
                  />
                ))}
              </div>
            </div>

            <button type="submit" className="btn-primary w-full" disabled={submit || code.length !== 6}>
              {submit ? <><Loader2 className="h-4 w-4 animate-spin" /> Verifying…</> : 'Verify and continue'}
            </button>
          </form>

          <div className="mt-6 flex items-center justify-between text-xs text-slate-600">
            <button
              type="button"
              onClick={onResend}
              disabled={resending}
              className="font-medium text-sky-700 hover:text-sky-800 disabled:opacity-60"
            >
              {resending ? 'Sending…' : 'Resend code'}
            </button>
            <Link to="/login" className="font-medium text-slate-600 hover:text-slate-800">
              Back to sign-in
            </Link>
          </div>

          <div className="mt-8 border-t border-slate-200 pt-6 text-xs text-slate-500">
            Didn't get an email? Check your spam folder, or use the resend link above.
            Codes expire after 10 minutes.
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * Where to send someone after they verify.
 *
 * This was a hand-kept copy of ROLE_HOMES and it had drifted: judges,
 * magistrates and prosecutors were sent to /hub, which the API gates to
 * hub_admin, and platform admins likewise — so verifying an OTP could land
 * you straight on a permissions error. Reading the single map means it
 * cannot drift again.
 */
function pickHomeForUser(me) {
  const roles = me?.roles ?? [];
  for (const r of roles) if (ROLE_HOMES[r]) return ROLE_HOMES[r];
  return null;
}
